import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import SecurityIcon from "@mui/icons-material/Security";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid2";
import SospitaLogo from "./CustomIcons";
import { useTranslation } from "react-i18next";

const ContentContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "column",
  alignSelf: "center",
  gap: theme.spacing(1),
  maxWidth: 540,
  width: "100%",
  padding: theme.spacing(1.5),
  height: "100%",
  display: "flex",
  justifyContent: "center",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
  "& svg": {
    fontSize: "3rem !important",
  },
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  transition: theme.transitions.create(["box-shadow", "transform"], {
    duration: theme.transitions.duration.short,
  }),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  minHeight: 100,
  "&:hover": {
    boxShadow: theme.shadows[3],
    transform: "translateY(-1px)",
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44, // Slightly increased from 40
  height: 44,
  borderRadius: theme.spacing(1),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.main}25)`,
  marginBottom: theme.spacing(1.5), // Increased margin
  flexShrink: 0,
  "& svg": {
    fontSize: 22, // Slightly increased from 20
    color: theme.palette.primary.main,
  },
}));

const items = [
  {
    icon: <SettingsSuggestRoundedIcon />,
    titleKey: "streamlinedPermitTitle",
    descriptionKey: "streamlinedPermitDescription",
    color: "primary",
  },
  {
    icon: <SecurityIcon />,
    titleKey: "proactiveObservationTitle",
    descriptionKey: "proactiveObservationDescription",
    color: "secondary",
  },
  {
    icon: <AnalyticsIcon />,
    titleKey: "dataDrivenSafetyTitle",
    descriptionKey: "dataDrivenSafetyDescription",
    color: "success",
  },
  {
    icon: <AutoFixHighRoundedIcon />,
    titleKey: "complianceAccessibilityTitle",
    descriptionKey: "complianceAccessibilityDescription",
    color: "info",
  },
];

export default function Content() {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <ContentContainer>
      {/* Compact Logo */}
      <LogoContainer>
        <SospitaLogo />
      </LogoContainer>

      {/* Compact Hero Section */}
      <Box sx={{ textAlign: "center", mb: 1.5 }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: "clamp(1.25rem, 2.5vw, 1.5rem)",
            color: "text.primary",
            mb: 0.5,
            lineHeight: 1.2,
          }}
        >
          {t("content.heroTitle", "Enterprise Safety Management")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontWeight: 400,
            fontSize: "0.875rem",
            lineHeight: 1.4,
            mb: 2,
            wordWrap: "break-word",
          }}
        >
          {t(
            "content.heroSubtitle",
            "Streamline safety processes with intelligent workflows and real-time insights"
          )}
        </Typography>
      </Box>

      {/* Flexible Feature Grid */}
      <Grid container spacing={2} sx={{ mb: 1.5 }}>
        {items.map((item, index) => (
          <Grid size={{ xs: 12, sm: 6 }} key={index}>
            <FeatureCard elevation={1}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  gap: 1.5,
                }}
              >
                {/* Icon and Title Section */}
                <Box
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                >
                  <IconContainer
                    sx={{
                      background: `linear-gradient(135deg, ${
                        theme.palette[item.color]?.main ||
                        theme.palette.primary.main
                      }15, ${
                        theme.palette[item.color]?.main ||
                        theme.palette.primary.main
                      }25)`,
                      "& svg": {
                        color:
                          theme.palette[item.color]?.main ||
                          theme.palette.primary.main,
                      },
                    }}
                  >
                    {item.icon}
                  </IconContainer>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        color: "text.primary",
                        lineHeight: 1.3,
                        wordWrap: "break-word",
                        hyphens: "auto",
                      }}
                    >
                      {t(`content.${item.titleKey}`)}
                    </Typography>
                  </Box>
                </Box>

                {/* Description Section */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.4,
                    fontSize: "0.8rem",
                    flex: 1,
                    wordWrap: "break-word",
                    hyphens: "auto",
                    overflow: "hidden",
                  }}
                >
                  {t(`content.${item.descriptionKey}`)}
                </Typography>
              </Box>
            </FeatureCard>
          </Grid>
        ))}
      </Grid>

      {/* Compact Trust Badge */}
      <Box
        sx={{
          textAlign: "center",
          p: 1.5,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.primary.main}12)`,
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.primary.main}20`,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            fontWeight: 500,
            fontSize: "0.75rem",
            wordWrap: "break-word",
          }}
        >
          <SecurityIcon sx={{ fontSize: 14 }} />
          {t("content.trustBadge", "Trusted by industry leaders worldwide")}
        </Typography>
      </Box>
    </ContentContainer>
  );
}
